import { IHixCategoryMapping, IHixDocumentMapping, IHixTaskMapping } from "../../data-structures/interfaces";

// HIX vocabularies and the mappings panel's row/state keys, shared by HixMappingsPanel and the
// default-mappings helpers (hixDefaultMappings.ts).

// The HIX-standard task types, always shown first in this exact order. The Dutch labels are
// domain terms (identical in every locale), not UI chrome — hence not in LocalizationModule.
export const STANDARD_TASK_TYPES: { key: string; label: string }[] = [
    { key: "vat_approval", label: "BTW-aangifte" },
    { key: "ict_approval", label: "ICP-aangifte" },
    { key: "vpb_approval", label: "VPB-aangifte" },
    { key: "ihz_approval", label: "IB-aangifte" },
    { key: "jaarwerk_approval", label: "Publicatiedocument" },
    { key: "wage_tax_approval", label: "Loonbelasting" },
    { key: "document_approval", label: "Documentgoedkeuring" },
    { key: "audit_report", label: "Auditrapport" },
    { key: "sba_message", label: "SBA" },
    { key: "sbr_yearwork", label: "SBR-jaarrekening" },
    { key: "dividend_tax", label: "Dividendbelasting" },
    { key: "yearwork_documents", label: "Jaarwerk" },
];

export const CUSTOM_TASK_PREFIX = "custom:";
export const customTaskTypeKey = (uploadTypeId: number) => `${CUSTOM_TASK_PREFIX}${uploadTypeId}`;
/** A custom task-type row ("custom:{hixUploadTypeId}"), live or stale. */
export const isCustomTaskRowKey = (rowKey: string) => rowKey.startsWith(CUSTOM_TASK_PREFIX);

export interface IDocumentTypeDef {
    key: string;
    label: string;
    description: string;
}

// The HIX document (file) types — a fixed list, HIX has no discovery API for these. Labels are
// Dutch domain terms (like STANDARD_TASK_TYPES); descriptions paraphrase the HIX documentation
// and are shown as-is.
export const DOCUMENT_TYPE_GROUPS: {
    headerKey: "DocTypesDeclarations" | "DocTypesYearwork" | "DocTypesSystem" | "DocTypesOther";
    types: IDocumentTypeDef[];
}[] = [
    {
        headerKey: "DocTypesDeclarations",
        types: [
            { key: "corporate_tax_declaration", label: "VPB-aangifte", description: "Corporate income tax return (Vennootschapsbelasting / VPB), XBRL." },
            { key: "income_tax_declaration", label: "IB-aangifte", description: "Personal income tax return (Inkomstenbelasting / IB)." },
            { key: "vat_declaration", label: "BTW-aangifte", description: "VAT / turnover tax return (BTW / Omzetbelasting), XBRL." },
            { key: "vat_declaration_supplementation", label: "BTW-suppletie", description: "VAT supplementary return (BTW-suppletie), XBRL." },
            { key: "ict_declaration", label: "ICP-aangifte", description: "Intra-Community Transactions return (ICP / ICT), XBRL." },
            { key: "wage_tax_declaration", label: "Loonbelasting", description: "Payroll/wage tax return (Loonbelasting), XML." },
            { key: "dividend_tax", label: "Dividendbelasting", description: "Dividend tax return, XBRL." },
        ],
    },
    {
        headerKey: "DocTypesYearwork",
        types: [
            { key: "publication_document", label: "Publicatiedocument (XBRL)", description: "Annual accounts filed with the Chamber of Commerce (Jaarrekening), XBRL — the version actually sent to Logius." },
            { key: "publication_document_pdf", label: "Publicatiedocument (PDF, SBR Nexus)", description: "PDF sent to the banks for the SBR Nexus flow." },
            { key: "annual_report", label: "Jaarrapport (PDF)", description: "Annual report as a PDF (not sent to Logius)." },
            { key: "audit_report", label: "Accountantsverklaring", description: "Auditor's statement / accountant's report (Accountantsverklaring), XBRL." },
            { key: "deposit_details", label: "Deponeringsgegevens", description: "KvK deposit/filing details (Deponeringsgegevens), XHTML." },
            { key: "sbr_yearwork", label: "SBR-jaarrekening", description: "SBR-based yearwork document (XBRL) — grouped with yearwork but excluded from the Logius/KvK filenaming flow." },
            { key: "notulen_AVA", label: "Notulen AVA", description: "Minutes of the general shareholders' meeting (Notulen Algemene Vergadering van Aandeelhouders)." },
            { key: "LOR", label: "LOR", description: "Letter of Representation (management representation letter accompanying the audit)." },
        ],
    },
    {
        headerKey: "DocTypesSystem",
        types: [
            { key: "signature_xml", label: "Digitale handtekening (XAdES)", description: "Detached digital signature (XAdES) in XML form." },
            { key: "sepa_salary", label: "SEPA-betaalbestand (salaris)", description: "SEPA payment file for salary payments, XML." },
            { key: "sepa_tax", label: "SEPA-betaalbestand (belasting)", description: "SEPA payment file for tax payments, XML." },
            { key: "sba_message", label: "SBA-bericht", description: "\"Service Berichten Aanslagen\" — tax assessment service messages from the tax authority, XBRL." },
        ],
    },
    {
        headerKey: "DocTypesOther",
        types: [
            { key: "summary", label: "Samenvatting", description: "The human-readable file HIX generates for the user (usually a PDF summary of a declaration)." },
            { key: "audit_log", label: "Auditlog", description: "Audit log of the task as a PDF — generated by HIX, not uploaded by the accountant." },
            { key: "attachment", label: "Bijlage", description: "An extra file added by the accountant (usually a PDF)." },
            { key: "generic_xml", label: "Generieke XML/XBRL", description: "Any XML/XBRL file that doesn't fit one of the specific types." },
        ],
    },
];

// The HIX template/question categories — the same fixed set for both, no discovery API.
// Labels match the HIX UI (see the template/question category dropdowns).
export const HIX_CATEGORIES: { key: string; label: string }[] = [
    { key: "bookkeeping", label: "Bookkeeping" },
    { key: "wage", label: "Wages" },
    { key: "ocr", label: "OCR" },
    { key: "fiscal", label: "Fiscal" },
    { key: "other", label: "Other" },
    { key: "yearwork", label: "Yearwork" },
];

// Rail/state key prefix for document-type rows: keys like "sba_message", "audit_report",
// "dividend_tax" and "sbr_yearwork" exist as BOTH a task type and a document type, so document
// rows are namespaced to keep the shared mapping/ignore state collision-free. The prefix never
// reaches the saved payload — document entries are stored under documentTypeKey without it.
// Template/question category rows are namespaced the same way (the two category sets share
// the same keys, so each gets its own prefix).
export const DOC_ROW_PREFIX = "doc:";
export const documentRowKey = (docTypeKey: string) => `${DOC_ROW_PREFIX}${docTypeKey}`;
export const documentTypeKeyFromRow = (rowKey: string) => rowKey.substring(DOC_ROW_PREFIX.length);
export const isDocumentRowKey = (rowKey: string) => rowKey.startsWith(DOC_ROW_PREFIX);

export const TEMPLATE_CATEGORY_ROW_PREFIX = "tplcat:";
export const templateCategoryRowKey = (categoryKey: string) => `${TEMPLATE_CATEGORY_ROW_PREFIX}${categoryKey}`;
export const templateCategoryKeyFromRow = (rowKey: string) => rowKey.substring(TEMPLATE_CATEGORY_ROW_PREFIX.length);
export const isTemplateCategoryRowKey = (rowKey: string) => rowKey.startsWith(TEMPLATE_CATEGORY_ROW_PREFIX);

export const QUESTION_CATEGORY_ROW_PREFIX = "qstcat:";
export const questionCategoryRowKey = (categoryKey: string) => `${QUESTION_CATEGORY_ROW_PREFIX}${categoryKey}`;
export const questionCategoryKeyFromRow = (rowKey: string) => rowKey.substring(QUESTION_CATEGORY_ROW_PREFIX.length);
export const isQuestionCategoryRowKey = (rowKey: string) => rowKey.startsWith(QUESTION_CATEGORY_ROW_PREFIX);

export const isTaskRowKey = (rowKey: string) =>
    !isDocumentRowKey(rowKey) && !isTemplateCategoryRowKey(rowKey) && !isQuestionCategoryRowKey(rowKey);

export const DOCUMENT_TYPE_BY_ROW_KEY = new Map<string, IDocumentTypeDef>(
    DOCUMENT_TYPE_GROUPS.flatMap((g) => g.types).map((t) => [documentRowKey(t.key), t])
);

export const KNOWN_CATEGORY_KEYS = new Set(HIX_CATEGORIES.map((c) => c.key));

// One mapping per (task type, site-design type); state is keyed on the composite.
export const mappingKey = (taskTypeKey: string, typeKey: string) => `${taskTypeKey}::${typeKey}`;

// Document and category mappings are held in the same in-memory record (and edited by the
// same machinery) as task mappings, carrying the prefixed row key in taskTypeKey; they are
// converted back to their clean saved shapes on save/canonicalize.
export const toInternalDocumentMapping = (m: IHixDocumentMapping): IHixTaskMapping => ({
    taskTypeKey: documentRowKey(m.documentTypeKey),
    isCustom: false,
    typeKey: m.typeKey,
    libraryTitle: m.libraryTitle,
    fields: m.fields ?? [],
});

export const toInternalCategoryMapping = (m: IHixCategoryMapping, rowKey: (categoryKey: string) => string): IHixTaskMapping => ({
    taskTypeKey: rowKey(m.categoryKey),
    isCustom: false,
    typeKey: m.typeKey,
    libraryTitle: m.libraryTitle,
    fields: m.fields ?? [],
});

// The column types a mapped value is written to (spec 2026-10-09 §1.1); Provisioning's
// HixFileDeliveryService.SupportedColumnTypes is the same set. Other columns are not offered.
export const SUPPORTED_FIELD_TYPES = ["Text", "Note", "Choice", "Number", "Integer", "Currency", "Boolean", "DateTime"];
